import * as ScrollArea from '@radix-ui/react-scroll-area';
import {
  cloneElement,
  type ComponentPropsWithRef,
  forwardRef,
  type ReactElement,
} from 'react';

import { cn } from '~/chords/lib/cn';

export interface ScrollbarsProps
  extends Omit<ComponentPropsWithRef<'div'>, 'className' | 'ref'> {
  children: ReactElement;
  asChild?: boolean;
}

const CORNER_CLASS = cn(
  'min-h-[0.75rem] min-w-[0.75rem] bg-level-03 hover:min-h-[1rem] hover:min-w-[1rem]',
);

const SCROLLBAR_CLASS = cn(
  'group select-none opacity-stronger',
  'hover:bg-emphasis-strong',
  'p-[0.2rem] hover:p-[0.25rem]',
  'transition-scrollbars duration-300',
  CORNER_CLASS,
);

const THUMB_CLASS = cn(
  'min-h-[0.35rem] rounded-full transition-colors duration-300 bg-default-inverse/stronger group-hover:min-h-[0.5rem] group-hover:bg-default-inverse',
);

export const Scrollbars = forwardRef<HTMLDivElement, ScrollbarsProps>(
  ({ children, asChild = false }, ref) => {
    /**
     * There's a bug in Radix's ScrollArea.Viewport (v1.2.10+) where the asChild prop
     * doesn't merge with the child Slot, resulting in two DIV elements instead of one,
     * with the parent DIV having an inline style of 'display: table'.
     * The '!block' and the accompanying code below is a workaround to fix this issue.
     * https://github.com/radix-ui/primitives/issues/1666
     * https://github.com/radix-ui/primitives/issues/926#issuecomment-2763624511
     */
    const child = asChild
      ? cloneElement(children, {
          className: cn(
            '!block max-h-radix-scroll',
            children?.props?.className,
          ),
        })
      : children;
    const childProps = asChild ? child.props : {};

    return (
      <ScrollArea.Root className="grow overflow-hidden">
        <ScrollArea.Viewport asChild={asChild} {...childProps} ref={ref}>
          {asChild ? child.props.children : child}
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar
          orientation="vertical"
          className={SCROLLBAR_CLASS}
        >
          <ScrollArea.Thumb className={THUMB_CLASS} />
        </ScrollArea.Scrollbar>
        <ScrollArea.Scrollbar
          orientation="horizontal"
          className={SCROLLBAR_CLASS}
        >
          <ScrollArea.Thumb className={THUMB_CLASS} />
        </ScrollArea.Scrollbar>
        <ScrollArea.Corner className={CORNER_CLASS} />
      </ScrollArea.Root>
    );
  },
);

Scrollbars.displayName = 'Scrollbars';
